import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2 } from 'lucide-react';
import { purchasesAPI, suppliersAPI } from '../../../api';
import { SORTED_CATEGORIES } from '../../../constants/accountingCategories';
import { useToast } from '../../../hooks/useToast';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { FormLabel } from '../../../components/ui/FormLabel';

const createDefaultItem = () => ({
  id: `item-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  description: '',
  partNumber: '',
  quantity: 1,
  unitPrice: '',
  category: '5000',
  notes: '',
});

const createPurchaseNumber = () => {
  const timestamp = Date.now().toString().slice(-6);
  return `PUR-${timestamp}`;
};

export default function MobilePurchaseManualEntry() {
  const navigate = useNavigate();
  const { success, error: showError, ToastContainer } = useToast();
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);
  const [saving, setSaving] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [formData, setFormData] = useState({
    supplierId: '',
    purchaseNum: createPurchaseNumber(),
    receiptNumber: '',
    supplierRef: '',
    purchaseDate: new Date().toISOString().slice(0, 10),
    deliveryDate: '',
    vatRate: '20',
    notes: '',
  });
  const [items, setItems] = useState([createDefaultItem()]);

  useEffect(() => {
    const fetchSuppliers = async () => {
      try {
        setLoadingSuppliers(true);
        const response = await suppliersAPI.getAll({ limit: 200, is_active: true });
        setSuppliers(response?.suppliers || []);
      } catch (err) {
        showError('Failed to load suppliers', err.message || 'Please try again');
      } finally {
        setLoadingSuppliers(false);
      }
    };
    fetchSuppliers();
  }, [showError]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleItemChange = (index, field, value) => {
    setItems((prev) => prev.map((item, itemIndex) => (
      itemIndex === index ? { ...item, [field]: value } : item
    )));
  };

  const addItem = () => {
    setItems((prev) => [...prev, createDefaultItem()]);
  };

  const removeItem = (index) => {
    setItems((prev) => {
      if (prev.length === 1) return prev;
      return prev.filter((_, itemIndex) => itemIndex !== index);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.supplierId) {
      showError('Supplier is required');
      return;
    }
    const cleanedItems = items
      .map((item) => ({
        description: item.description.trim(),
        part_number: item.partNumber?.trim() || null,
        quantity: Number(item.quantity) || 0,
        unit_price: Number(item.unitPrice) || 0,
        category: item.category || null,
        notes: item.notes?.trim() || null,
      }))
      .filter((item) => item.description);

    if (cleanedItems.length === 0) {
      showError('At least one item is required');
      return;
    }

    try {
      setSaving(true);
      const payload = {
        supplier_id: formData.supplierId,
        purchase_num: formData.purchaseNum.trim(),
        receipt_number: formData.receiptNumber.trim() || null,
        supplier_ref: formData.supplierRef.trim() || null,
        purchase_date: formData.purchaseDate || null,
        delivery_date: formData.deliveryDate || null,
        vat_rate: Number(formData.vatRate) || 0,
        notes: formData.notes.trim() || null,
        items: cleanedItems,
      };
      const result = await purchasesAPI.create(payload);
      const createdPurchaseId = result?.purchase?.id || result?.id;
      success('Purchase saved');
      if (createdPurchaseId) {
        navigate(`/tenant/mobile/receipts/payment-prompt?purchaseId=${encodeURIComponent(createdPurchaseId)}`);
      } else {
        navigate('/tenant/mobile');
      }
    } catch (err) {
      showError('Failed to save purchase', err.message || 'Please try again');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="px-4 py-6 pb-8">
      <ToastContainer />
      <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
        Manual Purchase Entry
      </h1>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
        Quickly log a purchase without using receipt upload.
      </p>

      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <FormLabel required className="text-gray-700 dark:text-gray-300">
            Supplier
          </FormLabel>
          <select
            name="supplierId"
            value={formData.supplierId}
            onChange={handleFormChange}
            required
            disabled={loadingSuppliers || saving}
            className="min-h-[44px] w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Select supplier</option>
            {suppliers.map((supplier) => (
              <option key={supplier.id} value={supplier.id}>
                {supplier.supplierName || supplier.supplier_name}
              </option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <FormLabel required className="text-gray-700 dark:text-gray-300">
              Purchase number
            </FormLabel>
            <Input
              name="purchaseNum"
              value={formData.purchaseNum}
              onChange={handleFormChange}
              required
              className="min-h-[44px] border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>
          <div>
            <FormLabel className="text-gray-700 dark:text-gray-300">
              Receipt number
            </FormLabel>
            <Input
              name="receiptNumber"
              value={formData.receiptNumber}
              onChange={handleFormChange}
              className="min-h-[44px] border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <FormLabel className="text-gray-700 dark:text-gray-300">
              Purchase date
            </FormLabel>
            <Input
              type="date"
              name="purchaseDate"
              value={formData.purchaseDate}
              onChange={handleFormChange}
              className="min-h-[44px] border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>
          <div>
            <FormLabel className="text-gray-700 dark:text-gray-300">
              Delivery date
            </FormLabel>
            <Input
              type="date"
              name="deliveryDate"
              value={formData.deliveryDate}
              onChange={handleFormChange}
              className="min-h-[44px] border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <FormLabel className="text-gray-700 dark:text-gray-300">
              Supplier ref
            </FormLabel>
            <Input
              name="supplierRef"
              value={formData.supplierRef}
              onChange={handleFormChange}
              className="min-h-[44px] border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>
          <div>
            <FormLabel className="text-gray-700 dark:text-gray-300">
              VAT rate %
            </FormLabel>
            <Input
              type="number"
              name="vatRate"
              min="0"
              max="100"
              step="0.01"
              value={formData.vatRate}
              onChange={handleFormChange}
              className="min-h-[44px] border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-medium text-gray-900 dark:text-gray-100">Items</h2>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="min-h-[36px]"
              onClick={addItem}
              disabled={saving}
            >
              <Plus className="h-4 w-4 mr-1" aria-hidden />
              Add item
            </Button>
          </div>

          {items.map((item, index) => (
            <div
              key={item.id}
              className="space-y-3 rounded-lg border border-gray-200 dark:border-gray-700 p-3"
            >
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Item {index + 1}</p>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeItem(index)}
                  disabled={items.length === 1 || saving}
                  className="min-h-[32px] text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                >
                  <Trash2 className="h-4 w-4" aria-hidden />
                </Button>
              </div>

              <div>
                <FormLabel required className="text-gray-700 dark:text-gray-300">
                  Description
                </FormLabel>
                <Input
                  value={item.description}
                  onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                  required
                  className="min-h-[44px] border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <FormLabel className="text-gray-700 dark:text-gray-300">Part number</FormLabel>
                  <Input
                    value={item.partNumber}
                    onChange={(e) => handleItemChange(index, 'partNumber', e.target.value)}
                    className="min-h-[44px] border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                </div>
                <div>
                  <FormLabel className="text-gray-700 dark:text-gray-300">Qty</FormLabel>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={item.quantity}
                    onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                    className="min-h-[44px] border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                </div>
                <div className="col-span-2">
                  <FormLabel className="text-gray-700 dark:text-gray-300">Unit price</FormLabel>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={item.unitPrice}
                    onChange={(e) => handleItemChange(index, 'unitPrice', e.target.value)}
                    className="min-h-[44px] border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                </div>
              </div>

              <div>
                <FormLabel className="text-gray-700 dark:text-gray-300">Category</FormLabel>
                <select
                  value={item.category}
                  onChange={(e) => handleItemChange(index, 'category', e.target.value)}
                  className="min-h-[44px] w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select category</option>
                  {SORTED_CATEGORIES.map((category) => (
                    <option key={category.code} value={category.code}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <FormLabel className="text-gray-700 dark:text-gray-300">Item notes</FormLabel>
                <textarea
                  value={item.notes}
                  onChange={(e) => handleItemChange(index, 'notes', e.target.value)}
                  rows={2}
                  className="min-h-[44px] w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          ))}
        </div>

        <div>
          <FormLabel className="text-gray-700 dark:text-gray-300">Notes</FormLabel>
          <textarea
            name="notes"
            value={formData.notes}
            onChange={handleFormChange}
            rows={3}
            className="min-h-[44px] w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div className="grid grid-cols-2 gap-3 pt-2">
          <Button
            type="button"
            variant="outline"
            className="min-h-[44px]"
            onClick={() => navigate('/tenant/mobile/receipts')}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            className="min-h-[44px]"
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save purchase'}
          </Button>
        </div>
      </form>
    </div>
  );
}
